import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playKey, playBeep } from "@/lib/sound";

const LINES = [
  "TRADRQUANT TERMINAL // v2.4",
  "BOOT SEQUENCE INITIATED ...",
  "> loading confluence engine .............. OK",
  "> linking exchanges [BINANCE·BYBIT·OKX] ... OK",
  "> calibrating 8 independent strategies .... OK",
  "> scanning 800+ assets / 5 timeframes ..... OK",
  "> QUANTIFY THE MARKET · CAPTURE THE EDGE",
];

export const BootSequence = () => {
  const booted = typeof window !== "undefined" && (sessionStorage.getItem("tq_booted") === "1" || window.location.search.includes("boot=0"));
  const [done, setDone] = useState(booted);
  const [visible, setVisible] = useState(0);
  const [progress, setProgress] = useState(0);

  const finish = useCallback(() => {
    sessionStorage.setItem("tq_booted", "1");
    playBeep();
    setDone(true);
  }, []);

  useEffect(() => {
    if (done) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finish();
      return;
    }
    const t1 = setInterval(() => setVisible((v) => {
      const next = Math.min(v + 1, LINES.length);
      if (next > v) playKey();
      return next;
    }), 210);
    const t2 = setInterval(() => setProgress((p) => Math.min(p + 5, 100)), 75);
    const end = setTimeout(finish, 2100);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
      clearTimeout(end);
    };
  }, [done, finish]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="boot-sequence"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] bg-[#050505] flex items-center justify-center px-6"
        >
          <div className="crt-scan" />
          <div className="w-full max-w-xl">
            <div className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8">
              <div className="flex items-center gap-2 mb-6">
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/60" />
              </div>
              <div className="font-mono text-xs md:text-sm space-y-1.5 min-h-[180px]">
                {LINES.slice(0, visible).map((l, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={i === 0 ? "text-[#BACD1C] tracking-[0.2em]" : i === LINES.length - 1 ? "text-[#BACD1C] tracking-[0.2em] pt-2" : "text-white/60"}
                  >
                    {l}
                    {i === visible - 1 && <span className="blink-cursor" />}
                  </motion.p>
                ))}
              </div>
              <div className="mt-6 h-1 w-full bg-white/10 overflow-hidden">
                <div className="boot-bar-fill h-full bg-[#BACD1C] transition-[width] duration-75" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
                <span>System check</span>
                <span>{progress}%</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
